import { forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface EnhancedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helperText?: string;
  showRequired?: boolean;
  'aria-describedby'?: string;
}

const EnhancedInput = forwardRef<HTMLInputElement, EnhancedInputProps>(
  ({ label, error, helperText, showRequired, className, id, ...props }, ref) => {
    const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    
    return (
      <div className="space-y-2">
        <Label htmlFor={inputId} className="text-solr-starfield-white">
          {label}
          {showRequired && <span className="text-red-400 ml-1" aria-label="required">*</span>}
        </Label>
        <Input
          ref={ref}
          id={inputId}
          className={cn(
            "transition-colors",
            error 
              ? "border-red-400 focus:border-red-400 focus:ring-red-400/20" 
              : "focus:border-solr-nebula-purple focus:ring-solr-nebula-purple/20",
            className
          )}
          aria-invalid={!!error}
          aria-describedby={cn(
            error && errorId,
            helperText && helperId,
            props['aria-describedby']
          ).trim() || undefined}
          {...props}
        />
        {helperText && (
          <p id={helperId} className="text-sm text-solr-starfield-white/70">
            {helperText}
          </p>
        )}
        {error && (
          <p id={errorId} className="text-sm text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

EnhancedInput.displayName = 'EnhancedInput';

export default EnhancedInput;